import { signEvent } from './event.js';
import { getPublicKey } from './keys.js';
import { now } from './util.js';

const AUTH_KIND = 22242;

/**
 * Minimal NIP-01 relay client, used by both the sender and the subscriber side.
 *
 * Uses the platform WebSocket (native in Node >= 22 and in browsers), so the
 * same code runs in a CLI, a service and a web app with no adapter.
 */
export class HeraldRelay {
  /**
   * @param {string} url
   * @param {{authKey?:Uint8Array|string, WebSocketImpl?:any, timeoutMs?:number}} [opts]
   */
  constructor(url, opts = {}) {
    this.url = url;
    this.authKey = opts.authKey ?? null;
    this.WebSocketImpl = opts.WebSocketImpl ?? globalThis.WebSocket;
    this.timeoutMs = opts.timeoutMs ?? 10000;

    this.ws = null;
    this.challenge = null;
    this.authedAs = null;
    this.notices = [];
    this.subCounter = 0;
    this._authPromise = null;
    /** @type {Map<string, {onEvent:function, onEose?:function, onClosed?:function}>} */
    this.subs = new Map();
    /** @type {Map<string, {resolve:function}>} */
    this.pendingOk = new Map();
  }

  connect() {
    if (this.ws) return this.ready;
    this.ready = new Promise((resolve, reject) => {
      const ws = new this.WebSocketImpl(this.url);
      this.ws = ws;
      const timer = setTimeout(() => reject(new Error(`timed out connecting to ${this.url}`)), this.timeoutMs);

      ws.onopen = () => { clearTimeout(timer); resolve(this); };
      ws.onerror = (err) => { clearTimeout(timer); reject(new Error(`relay connection failed: ${this.url}`)); };
      ws.onclose = () => {
        for (const { resolve: r } of this.pendingOk.values()) r({ ok: false, message: 'connection closed' });
        this.pendingOk.clear();
      };
      ws.onmessage = (event) => this.#onFrame(event.data);
    });
    return this.ready;
  }

  #onFrame(data) {
    let frame;
    try { frame = JSON.parse(typeof data === 'string' ? data : data.toString()); } catch { return; }
    const [type, ...rest] = frame;

    switch (type) {
      case 'EVENT': {
        const [subId, event] = rest;
        this.subs.get(subId)?.onEvent(event);
        break;
      }
      case 'EOSE': {
        this.subs.get(rest[0])?.onEose?.();
        break;
      }
      case 'OK': {
        const [id, accepted, message] = rest;
        this.pendingOk.get(id)?.resolve({ ok: accepted, message: message ?? '' });
        this.pendingOk.delete(id);
        break;
      }
      case 'CLOSED': {
        const [subId, message] = rest;
        this.subs.get(subId)?.onClosed?.(message);
        this.subs.delete(subId);
        break;
      }
      case 'AUTH': {
        this.challenge = rest[0];
        // Do not auto-authenticate: callers decide when to reveal an identity,
        // because anonymous reads are a privacy feature, not an oversight.
        break;
      }
      case 'NOTICE':
        this.notices.push(rest[0]);
        break;
    }
  }

  /**
   * NIP-42 auth is carried in an AUTH frame, not an EVENT frame -- an auth
   * event submitted as a normal event is (correctly) rejected by the relay's
   * admission policy, since kind 22242 is not a Herald message kind.
   *
   * Idempotent: concurrent callers share one in-flight handshake.
   */
  #authenticate() {
    if (this._authPromise) return this._authPromise;
    this._authPromise = (async () => {
      const event = signEvent({
        kind: AUTH_KIND,
        content: '',
        tags: [['relay', this.url], ['challenge', this.challenge]],
      }, this.authKey);
      const res = await this.#sendAwaitingOk(['AUTH', event], event.id);
      if (res.ok) this.authedAs = getPublicKey(this.authKey);
      else this._authPromise = null; // allow a retry
      return res;
    })();
    return this._authPromise;
  }

  /** Authenticate explicitly (NIP-42) and wait until the relay confirms. */
  async authenticate(secretKey) {
    if (secretKey && secretKey !== this.authKey) { this.authKey = secretKey; this._authPromise = null; }
    await this.connect();
    // The challenge arrives immediately after connect; wait briefly for it.
    for (let i = 0; i < 100 && !this.challenge; i++) await sleep(10);
    if (!this.challenge) throw new Error('relay did not offer an AUTH challenge');
    const res = await this.#authenticate();
    if (!res.ok) throw new Error(`relay rejected authentication: ${res.message}`);
    return res;
  }

  #send(frame) {
    if (!this.ws || this.ws.readyState !== 1) throw new Error('relay not connected');
    this.ws.send(JSON.stringify(frame));
  }

  /** Send a frame and resolve with the relay's OK frame for the given event id. */
  #sendAwaitingOk(frame, id) {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.pendingOk.delete(id);
        resolve({ ok: false, message: 'timed out waiting for relay OK' });
      }, this.timeoutMs);
      this.pendingOk.set(id, { resolve: (res) => { clearTimeout(timer); resolve(res); } });
      this.#send(frame);
    });
  }

  /** @returns {Promise<{ok:boolean, message:string}>} the relay's OK frame */
  async publish(event) {
    await this.connect();
    // If this connection carries credentials, finish the handshake first so
    // that consent-gated writes are not evaluated as anonymous.
    if (this.authKey && !this.authedAs && this.challenge) await this.#authenticate();
    return this.#sendAwaitingOk(['EVENT', event], event.id);
  }

  /** One-shot query: resolves with everything the relay has, at EOSE. */
  async query(filters, { timeoutMs = this.timeoutMs } = {}) {
    await this.connect();
    // Finish the NIP-42 handshake first when this connection is credentialed,
    // otherwise consent-gated kinds would be refused as anonymous reads.
    if (this.authKey && !this.authedAs && this.challenge) await this.#authenticate();
    const subId = `q${++this.subCounter}`;
    const events = [];
    return new Promise((resolve, reject) => {
      const finish = (err) => {
        clearTimeout(timer);
        this.subs.delete(subId);
        try { this.#send(['CLOSE', subId]); } catch { /* already closed */ }
        err ? reject(err) : resolve(events);
      };
      const timer = setTimeout(() => finish(null), timeoutMs);
      this.subs.set(subId, {
        onEvent: (e) => events.push(e),
        onEose: () => finish(null),
        onClosed: (message) => finish(new Error(`relay refused subscription: ${message}`)),
      });
      this.#send(['REQ', subId, ...(Array.isArray(filters) ? filters : [filters])]);
    });
  }

  /** Long-lived subscription. Returns a function that closes it. */
  async subscribe(filters, onEvent, { onEose, onClosed } = {}) {
    await this.connect();
    // Finish the NIP-42 handshake first when this connection is credentialed,
    // otherwise consent-gated kinds would be refused as anonymous reads.
    if (this.authKey && !this.authedAs && this.challenge) await this.#authenticate();
    const subId = `s${++this.subCounter}`;
    this.subs.set(subId, { onEvent, onEose, onClosed });
    this.#send(['REQ', subId, ...(Array.isArray(filters) ? filters : [filters])]);
    return () => {
      this.subs.delete(subId);
      try { this.#send(['CLOSE', subId]); } catch { /* already closed */ }
    };
  }

  close() {
    try { this.ws?.close(); } catch { /* ignore */ }
    this.ws = null;
    this.ready = null;
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Query several relays and merge by event id. Herald senders should publish to more than one. */
export async function queryMany(urls, filters, opts = {}) {
  const results = await Promise.allSettled(
    urls.map(async (url) => {
      const relay = new HeraldRelay(url, opts);
      try { return await relay.query(filters, opts); } finally { relay.close(); }
    }),
  );
  const byId = new Map();
  for (const res of results) {
    if (res.status !== 'fulfilled') continue;
    for (const event of res.value) byId.set(event.id, event);
  }
  return [...byId.values()];
}
